/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { ConnectionsCell } from "./ConnectionCell";
import { connectionIndex } from "./ConnectionsHelpers";

export type ConnectionRef = {
    id: string;
    subscriptionId: string;
    //NEW
    connectedServiceId?: number | string;
    connectedServiceType?: string;           
    connectedServiceSubId?: string;
    /** The datacenter of the port a FWInt rides on (CTX-1210); absent for anything else. */
    connectedServiceDatacenter?: string | null;
  };
export interface Fwaas {
    id: number;
    serviceId: string;
    name: string;
    tag: string[];
    customer?: {
        name: string
        id: number;
        chargebeeCustomerId: string
    }
    status: string;
    connections: {
        internet?: ConnectionRef[];
        cloudConnectivity?: ConnectionRef[];
        fwint?: ConnectionRef[];
        sdwanInt?: ConnectionRef[];
        port?: ConnectionRef[];
        datacenter?: ConnectionRef[];
        branch?: ConnectionRef[];
        raw?: string;
      };
    type?: string;
    // Raw subscription id, kept nullable for the detail; `serviceId` above is its
    // display form (CTX-732).
    subscriptionId?: string | null;
    term?: number | null;
    termUnit?: string | null;
    renewDate?: string | null;
}

type TagVariant = "success" | "default" | "error" | "warning";

const FWAAS_STATUS_TAG: Record<string, { variant: TagVariant; label: string }> = {
    up: { variant: "success", label: "Active" },
    down: { variant: "error", label: "Down" },
};

/**
 * A Virtual Firewall's service status, as the list and the detail both show it — one
 * mapping, so the two screens cannot word the same status differently (CTX-732).
 */
export function FwaasStatusTag({ status }: { status: string | null | undefined }) {
    const known = status ? FWAAS_STATUS_TAG[status] : undefined;
    const { variant, label } =
        known ?? { variant: status == null ? "default" : "warning", label: "Unknown" };
    return <GenericTag variant={variant} label={label} size="small" />;
}

export const fwaasColumns: GenericTableColumn<Fwaas>[] = [
    {
        header: "Service ID",
        accessor: "serviceId",
        renderCell: (row) => <div>{row.serviceId}</div>,
    },
    {
        header: "Status",
        renderCell: (row) => <FwaasStatusTag status={row.status} />,
    },
    {
        header: "Connection",
        accessor: "connections",
        sortAccessor: (row) => row.connections?.raw ?? "",
        renderCell: (row) => (
          <ConnectionsCell
            row={row}
          />
        ),
    },
];

const toConnArr = (x: unknown): ConnectionRef[] => {
  if (Array.isArray(x)) {
    return x.map((v: any) => {
      if (v && typeof v === "object") {
        const id = String(v.id ?? v.subscriptionId ?? "");
        const subscriptionId = String(v.subscriptionId ?? v.id ?? id);

        const cs = v.connectedService ?? {};
        const connectedServiceId =
          cs.id ?? v.connectedServiceId ?? undefined;
        const connectedServiceType =
          cs.type ?? v.connectedServiceType ?? undefined;
        const connectedServiceSubId =
          cs.subscriptionId ?? cs.name ?? v.connectedServiceSubId ?? undefined;
        const connectedServiceDatacenter = cs.datacenter ?? null;

        return {
          id,
          subscriptionId,
          connectedServiceId,
          connectedServiceType,
          connectedServiceSubId,
          connectedServiceDatacenter,
        };
      }
      const s = String(v ?? "");
      return { id: s, subscriptionId: s };
    });
  }
  if (x == null) return [];
  const s = String(x);
  return [{ id: s, subscriptionId: s }];
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapeFwaasData = (fwaas: any[]): Fwaas[] => {
    return fwaas.map((p) => {
        const customer = p.customer
        ? {
            name: p.customer.name ?? "-",
            id: p.customer.id ?? "-",
            chargebeeCustomerId: p.customer.chargebeeCustomerId ?? "-"
            }
        : undefined;

        const internet = toConnArr(p.connections?.internet);
        const cloudConnectivity = toConnArr(p.connections?.cloudConnectivity);
        const fwint = toConnArr(p.connections?.fwint);
        const sdwanInt = toConnArr(p.connections?.sdwanInt);
        const port = toConnArr(p.connections?.port);
        const datacenter = toConnArr(p.connections?.datacenter);

        const buckets = { internet, cloudConnectivity, fwint, sdwanInt, port, datacenter };
        // "Service ID" is the subscription id, as in the detail — not the service name.
        const serviceId = withFallback(p.subscriptionId);

    return {
        id: p.id,
        serviceId,
        name: withFallback(p.name),
        customer,
        status: withFallback(p.status),
        connections: {
            ...buckets,
            raw: connectionIndex({ connections: buckets }),
          },
        tag: Array.isArray(p.tag) ? p.tag : [withFallback(p.tag)],
        subscriptionId: p.subscriptionId ?? null,
        term: p.term ?? null,
        termUnit: p.term_unit ?? null,
        renewDate: p.renewDate ?? null,
    }});
  };

